<template>
<!-- eslint-disable max-len -->
<div class="radial-progress-nav" :style="labelBelow ? 'background-color: transparent; transform: scale(1.75);' : ''">
  <div v-if="!labelBelow" class="metric-name">
    <p
      class="metric-name-label"
      :style="game.metricTopBarLabelStyle ? game.metricTopBarLabelStyle : undefined"
    >{{ name }}</p>
  </div>
  <div
    class="radial-progress-ring-nav"
    :style="labelBelow ? `position: absolute; top: 0; left: 32px; background-color: #33333366; ${safariPdf ? 'box-shadow: 0px 0px 0px 0px white;' : 'box-shadow: 0px 0px 1px 5px white;'}` : ''"
  >
    <div
      class="circular-fill"
      :style="{
        '--fill': colorRGB,
        '--value': `${getPercentFromValue(displayValue)}%`,
        '--center': labelBelow ? 'var(--primaryColor)' : 'white'
      }"
    >
    </div>
  </div>
  <span
    class="value-flex"
    :style="labelBelow
      ? { position: 'absolute', top: 0, left: '32px', color: 'white' }
      : { color: 'black' }"
  >
    <div
      class="value"
      :style="labelBelow
        ? (game.metricReportPercentageStyle ? game.metricReportPercentageStyle : undefined)
        : (game.metricTopBarPercentageStyle ? game.metricTopBarPercentageStyle : undefined)"
    >
      <div>
        {{ displayValue.toFixed(decimalPlaces) }}
      </div>
      <div
        class="percent-sign"
        v-if="percent && decimalPlaces === 0"
      >
        %
      </div>
    </div>
  </span>
  <p
    class="min-label"
    v-if="false && min"
    :style="labelBelow ? { position: 'absolute', top: 0, left: '32px' } : {}"
  >
    {{ checkedMin }}
  </p>
  <p
    class="max-label"
    v-if="false && max && max !== 100"
    :style="labelBelow ? { position: 'absolute', top: 0, left: '32px' } : {}"
  >
    {{ checkedMax }}
  </p>
  <div
    v-if="labelBelow"
    class="metric-name"
    style="position: absolute; top: 90%; margin: auto; text-align: center;"
  >
    <p
      class="metric-name-label"
      style="margin: auto; text-align: center; font-size: 0.5em; width: 70%; line-height: 110%;"
      :style="game.metricReportLabelStyle ? game.metricReportLabelStyle : undefined"
    >{{ name }}</p>
  </div>
</div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  props: {
    value: { type: Number },
    percent: { type: Boolean },
    integer: { type: Boolean },
    labelBelow: { type: Boolean, default: false },
    pdf: { type: Boolean, default: false },
    min: { type: Number },
    max: { type: Number },
    color: { type: String },
    name: { type: String }
  },
  data() {
    return {
      displayValue: 0
    };
  },
  computed: {
    ...mapGetters([
      'activeEnvironment',
      'game'
    ]),
    safariPdf() {
      return this.pdf &&
        navigator.vendor && navigator.vendor.indexOf('Apple') > -1 &&
        navigator.userAgent &&
        navigator.userAgent.indexOf('CriOS') === -1 &&
        navigator.userAgent.indexOf('FxiOS') === -1;
    },
    clampedValue() {
      let { value } = this;
      if (this.percent) value = Math.max(0, Math.min(100, value));
      if (!isNaN(parseFloat(this.min))) {
        value = Math.max(this.min, value);
      }
      if (!isNaN(parseFloat(this.max))) {
        value = Math.min(this.max, value);
      }
      return value;
    },
    checkedMin() {
      return !isNaN(parseFloat(this.min)) && this.min || 0;
    },
    checkedMax() {
      return !isNaN(parseFloat(this.max)) && this.max || 100;
    },
    checkedRange() {
      return Math.abs(this.checkedMax - this.checkedMin) || 100;
    },
    displayAsInteger() {
      return this.percent || this.integer || this.displayValue >= 100 || this.displayValue <= -100;
    },
    decimalPlaces() {
      return (this.displayAsInteger ? 0 : 1);
    },
    multiplier() {
      return 10 ** this.decimalPlaces;
    },
    colorRGB() {
      return this.color.replace(/rgba?(\(\s*\d+\s*,\s*\d+\s*,\s*\d+)(?:\s*,.+?)?\)/, 'rgba$1,1)');
    },
  },
  created() {
    this.displayValue = this.clampedValue;
  },
  methods: {
    getDecimalFromValue(value) {
      // radial display only makes sense for values in a range
      // so assume percent or capped vals
      return Math.abs(value - this.checkedMin) / this.checkedRange;
    },
    getPercentFromValue(value) {
      const fraction = this.getDecimalFromValue(value);
      return fraction * 100;
    },
    getDegreesFromValue(value) {
      const fraction = this.getDecimalFromValue(value);
      return fraction * 360;
    },
    getTransform(degrees) {
      return `rotate(${degrees}deg`;
    },
    round(value) {
      return Math.round(value * this.multiplier) / this.multiplier;
    },
    ceil(value) {
      return Math.ceil(value * this.multiplier) / this.multiplier;
    },
    floor(value) {
      return Math.floor(value * this.multiplier) / this.multiplier;
    }
  },
  watch: {
    clampedValue(newValue, oldValue) {
      const target = this.round(newValue);
      const increase = target >= oldValue;
      const steps = 100;
      const duration = 2500;
      const step = (target - oldValue) / steps;
      const percentInterval = setInterval(() => {
        if (increase) {
          if (this.displayValue >= target) return clearInterval(percentInterval);
          this.displayValue = Math.min(target, this.ceil(this.displayValue + step));
        } else {
          if (this.displayValue <= target) return clearInterval(percentInterval);
          this.displayValue = Math.max(target, this.floor(this.displayValue + step));
        }
      }, duration / steps);
    }
  }
};
</script>

<style lang="scss" scoped>
.radial-progress-nav {
  position: relative;
  background-color: white;
  width: 450px;
  height: 232px;
  border-radius: 50%;
  display: inline-block;
  margin: 0 10px;
}
.radial-progress-ring-nav {
  background-color: #e5e5e5;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  top: 20px;
  left: 310px;
  position: absolute;
  box-shadow: 4px 3px 10px 1px #918287, 30px 30px 45px 10px #e6e6e6;
}
.circular-fill {
  --center: white;
  --fill: lightseagreen;
  --value: 0%;
  width: 200px;
  height: 200px;
  border: 32px solid transparent;
  border-radius: 50%;
  background:
    linear-gradient(var(--center),var(--center)) padding-box,
    conic-gradient(var(--fill) var(--value), #e5e5e5 var(--value)) border-box;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

// @keyframes progress-pulse {
//   0% {
//     background: hsla(255, 0%, 100%, 0);
//   }
//   50% {
//     background: hsla(255, 0%, 100%, 10%);
//   }
//   100% {
//     background: hsla(255, 0%, 100%, 0);;
//   }
// }

.value-flex {
  top: 20px;
  left: 310px;
  position: absolute;
  width: 200px;
  height: 200px;
  display: flex;
  flex-flow: row;
  justify-content: center;
  align-items: center;
  transform: translateZ(1px); // fix for full-screen
}
.value {
  font-size: 63px;
  font-weight: bold;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
}
.percent-sign {
  font-size: 60.3%; // 38px (relative to support inherited size change)
  padding-left: 2px;
}
.name {
  color: black;
  position: absolute;
  left: 5px;
  top: 144px;
  line-height: 16px;
  font-size: 15px;
  padding: 0 72px;
  font-weight: bold;
  overflow: hidden;
}
.min-label {
  position: absolute;
  left: 123px;
  top: 21px;
  color: white;
}
.max-label {
  position: absolute;
  right: 123px;
  top: 21px;
  color: white;
}
.metric-name {
  position: absolute;
  font-size: 50px;
  top: 50px;
  left: 0;
  display: block;
  height: calc(100% - 50px);
  width: 280px;
}
.metric-name-label {
  width: 100%;
  text-align: right;
}
</style>



// WEBPACK FOOTER //
// src/components/game/ui/RadialProgressNav.vue